import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircleOutlined as CheckIcon,
  CheckRounded as CheckRoundedIcon,
  ArrowBackOutlined as BackIcon,
  AccountCircleOutlined as UserIcon,
  BusinessOutlined as OrgIcon,
  PaymentsOutlined as PaymentIcon,
  ArrowForwardOutlined as ForwardIcon,
  ChevronRight as ChevronRightIcon,
  WorkspacePremiumOutlined as PlanIcon,
  StarRounded as StarIcon,
  HelpOutlineOutlined as HelpIcon,
  MenuBookOutlined as DocsIcon,
} from '@mui/icons-material';
import { getSignupConfig, submitSignup, verifySignupOtp, resendSignupVerification } from '../../services/stripeApi';
import { getAppUrl } from '../../utils/deploymentMode';
import CaptchaWidget from '../../components/CaptchaWidget';
import ContactSalesModal from './components/ContactSalesModal';

// Marketing palette — same constants used on LandingPage and the
// success page. Keep in sync so the public surface feels continuous.
const ACCENT      = '#0969da';
const ACCENT_DARK = '#0550ae';
const ACCENT_SOFT = 'rgba(9,105,218,0.08)';
const BORDER     = '#d0d7de';
const SURFACE     = '#ffffff';
const SURFACE_ALT = '#f6f8fa';
const INK         = '#1f2328';
const INK_SOFT    = '#57606a';
const INK_DIM     = '#8c959f';
const SUCCESS     = '#1a7f37';
const LOGO_SRC    = '/assets/realops.png';

const slugifyOrg = (s) =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+)|(-+$)/g, '')
    .slice(0, 50);

/**
 * Public signup flow.
 *
 * <p>Two-step UX:
 * <ol>
 *   <li><strong>Choose a plan</strong> — operator-curated pricing
 *       cards with full feature lists; the most popular plan is
 *       highlighted by an explicit chip + accent border.</li>
 *   <li><strong>Account details</strong> — workspace name + slug,
 *       admin credentials, captcha, terms.</li>
 * </ol>
 *
 * <p>Free plans land directly into the platform with a JWT.
 * Paid plans get a redirect to Stripe Checkout; the user comes back
 * via the configured success URL.
 *
 * <p>If the operator hasn't enabled signup, the page renders an
 * "unavailable" state and routes back to the landing page.
 */
const SignupPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifySent, setVerifySent] = useState(null); // email-verification confirmation message
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  // Set once the account is active (after OTP verify, or directly when email
  // verification is off) → renders the welcome + signup-summary screen.
  const [welcome, setWelcome] = useState(null);

  const [planCode, setPlanCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [orgSlugTouched, setOrgSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [agree, setAgree] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  // Captcha config (provider + public site key) — same source the login
  // and waitlist forms use, so the operator's Cloudflare Turnstile shows
  // on the account-creation step and the token is verified server-side.
  const [captcha, setCaptcha] = useState({ enabled: false });
  // Contact-sales popup: holds the plan the prospect is enquiring about
  // ({ code, name }), or null when closed.
  const [salesPlan, setSalesPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSignupConfig()
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        if (c?.enabled && c.plans && c.plans.length) {
          // Default selection — pick the "most popular" if marked,
          // otherwise the second card (mid-tier feels right), else first.
          const mostPopular = c.plans.find((p) => p.code && p.code.toLowerCase().includes('pro'));
          const middle = c.plans.length >= 3 ? c.plans[1] : c.plans[0];
          setPlanCode((mostPopular || middle).code);
        }
      })
      .catch(() => {
        if (!cancelled) setConfig({ enabled: false, plans: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Load captcha config so the create-account step renders the operator's
  // configured widget (Cloudflare Turnstile / hCaptcha / reCAPTCHA).
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/captcha-config')
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (!cancelled && c) setCaptcha(c); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Auto-derive slug from org name unless the user has typed one manually.
  useEffect(() => {
    if (!orgSlugTouched) setOrgSlug(slugifyOrg(orgName));
  }, [orgName, orgSlugTouched]);

  const selectedPlan = useMemo(
    () => config?.plans?.find((p) => p.code === planCode) || null,
    [config, planCode]
  );

  // Compute which card is "popular" — middle of the list when there
  // are 3 plans, or the one whose code contains 'pro'. Single source
  // of truth so the header chip and card border agree.
  const popularPlanCode = useMemo(() => {
    if (!config?.plans?.length) return null;
    const proPlan = config.plans.find((p) => p.code && p.code.toLowerCase().includes('pro'));
    if (proPlan) return proPlan.code;
    if (config.plans.length >= 3) return config.plans[1].code;
    return null;
  }, [config]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isDark ? '#0d1117' : SURFACE }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config?.enabled) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: isDark ? '#0d1117' : SURFACE }}>
        <TopBar isDark={isDark} loginHref={getAppUrl('/')} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <Container maxWidth="sm">
            <Card variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3, borderColor: isDark ? '#30363d' : BORDER }}>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                Sign up isn't open right now
              </Typography>
              <Typography variant="body1" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, mb: 3 }}>
                Public registration isn't currently open. Get in touch with the team to request early access.
              </Typography>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="outlined" onClick={() => navigate('/')}>
                  Back to home
                </Button>
                <Button variant="contained" onClick={() => navigate('/contact/new?type=GENERAL')}>
                  Contact us
                </Button>
              </Stack>
            </Card>
          </Container>
        </Box>
      </Box>
    );
  }

  const validateStep1 = () => {
    if (!planCode) return 'Please pick a plan';
    return null;
  };

  const validateStep2 = () => {
    if (!orgName.trim()) return 'Organisation name is required';
    if (orgSlug.length < 2) return 'Workspace URL must be at least 2 characters';
    if (!/^[a-z0-9-]+$/.test(orgSlug)) return 'Workspace URL can only contain lowercase letters, numbers, and dashes';
    if (!adminEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return 'A valid work email is required';
    if (adminPassword.length < 8) return 'Password must be at least 8 characters';
    if (adminPassword !== adminPasswordConfirm) return "Passwords don't match";
    if (!agree) return 'Please agree to the terms to continue';
    return null;
  };

  const handleSubmit = async () => {
    const v1 = validateStep1();
    if (v1) return setError(v1);
    const v2 = validateStep2();
    if (v2) return setError(v2);
    if (captcha?.enabled && captcha.provider && captcha.provider !== 'RECAPTCHA_V3' && !captchaToken) {
      return setError('Please complete the captcha to continue');
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await submitSignup({
        orgName: orgName.trim(),
        orgSlug: orgSlug.trim(),
        adminUsername: adminUsername.trim() || adminEmail.trim(),
        adminEmail: adminEmail.trim().toLowerCase(),
        adminPassword,
        planCode,
        captchaToken: captchaToken || undefined,
      });
      if (res.type === 'checkout' && res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      if (res.type === 'verify_email') {
        // Account created but gated on email verification — no token returned.
        setVerifySent(res.message || 'Check your email to verify your address and activate your account.');
        return;
      }
      if (res.token) {
        localStorage.setItem('authToken', res.token);
        if (res.orgSlug) localStorage.setItem('orgSlug', res.orgSlug);
      }
      // Email verification is off → account is already active. Go straight to
      // the welcome + summary screen.
      showWelcome(res);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Build the welcome/summary payload from the signup state + server response.
  const showWelcome = (res) => {
    setWelcome({
      firstName: (adminUsername || adminEmail).split(/[@\s]/)[0] || 'there',
      username: adminUsername.trim() || null,
      email: adminEmail.trim().toLowerCase(),
      orgName: res?.orgName || orgName,
      orgSlug: res?.orgSlug || orgSlug,
      planName: res?.planName || selectedPlan?.name,
      trialDays: res?.trialDays ?? (selectedPlan?.freeTier ? (selectedPlan?.trialDays || 14) : null),
    });
  };

  const handleVerifyOtp = async () => {
    const code = otp.trim().toUpperCase();
    if (code.length !== 6) return setOtpError('Enter the 6-character code from your email');
    setOtpError(null);
    setOtpSubmitting(true);
    try {
      const res = await verifySignupOtp({ email: adminEmail.trim().toLowerCase(), orgSlug: orgSlug.trim(), otp: code });
      if (res?.token) {
        localStorage.setItem('authToken', res.token);
        if (res.orgSlug) localStorage.setItem('orgSlug', res.orgSlug);
        showWelcome(res);
        return;
      }
      setOtpError('Could not verify — please try again.');
    } catch (e) {
      setOtpError(e?.response?.data?.message || 'That code is invalid or has expired.');
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setResendMsg(null);
    setOtpError(null);
    try {
      await resendSignupVerification({ email: adminEmail.trim().toLowerCase(), orgSlug: orgSlug.trim() });
      setResendMsg('A new code is on its way — check your inbox.');
    } catch (e) {
      setResendMsg('If that account needs activation, a new code has been sent.');
    }
  };

  if (welcome) {
    const panel = isDark ? '#0d1117' : SURFACE;
    const sub = isDark ? '#9da7b3' : INK_SOFT;
    const cardBorder = isDark ? '#30363d' : BORDER;
    const rows = [
      ['Workspace', welcome.orgName],
      ['Workspace URL', welcome.orgSlug],
      ['Plan', welcome.planName],
      welcome.trialDays ? ['Free trial', `${welcome.trialDays} days`] : null,
      ['Account owner', welcome.email],
      welcome.username && welcome.username.toLowerCase() !== welcome.email
        ? ['Sign-in username', welcome.username] : null,
    ].filter(Boolean);
    const nextSteps = [
      'Connect a data source — point OpenTelemetry or a VerOps agent at your stack',
      'Create your first synthetic or network check',
      'Set up an alert and invite your team',
    ];
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
        background: isDark
          ? 'radial-gradient(900px 380px at 50% -6%, rgba(9,105,218,0.18), transparent 60%), #0d1117'
          : 'radial-gradient(900px 380px at 50% -6%, rgba(9,105,218,0.07), transparent 60%), #ffffff',
        color: isDark ? '#e6edf3' : INK }}>
        <TopBar isDark={isDark} loginHref={getAppUrl('/')} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 3 } }}>
          <Box sx={{ maxWidth: 560, width: '100%' }}>
            {/* Hero */}
            <Stack alignItems="center" textAlign="center" spacing={1.25} sx={{ mb: 3 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(26,127,55,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <CheckRoundedIcon sx={{ color: SUCCESS, fontSize: 38 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                Welcome aboard, {welcome.firstName}! 🎉
              </Typography>
              <Typography sx={{ color: sub, maxWidth: 440, lineHeight: 1.6 }}>
                Your email is verified and <Box component="span" sx={{ fontWeight: 700, color: isDark ? '#e6edf3' : INK }}>{welcome.orgName}</Box> is live.
                We've emailed a copy of this summary to <strong>{welcome.email}</strong>.
              </Typography>
            </Stack>

            {/* Summary card */}
            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: cardBorder, bgcolor: panel, mb: 2 }}>
              <Typography variant="overline" sx={{ color: sub, fontWeight: 700, letterSpacing: '0.08em' }}>
                Your account
              </Typography>
              <Stack sx={{ mt: 1 }}>
                {rows.map(([label, value], i) => (
                  <Stack key={label} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ py: 1, borderBottom: i < rows.length - 1 ? '1px solid' : 'none', borderColor: cardBorder }}>
                    <Typography variant="body2" sx={{ color: sub }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>

            {/* What's next */}
            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: cardBorder, bgcolor: panel, mb: 3 }}>
              <Typography variant="overline" sx={{ color: sub, fontWeight: 700, letterSpacing: '0.08em' }}>
                What's next
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {nextSteps.map((s, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, fontWeight: 800, fontSize: 13,
                      bgcolor: 'rgba(9,105,218,0.12)', color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {i + 1}
                    </Box>
                    <Typography variant="body2" sx={{ color: isDark ? '#e6edf3' : INK }}>{s}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>

            <Button
              fullWidth variant="contained" size="large"
              href={getAppUrl('/')}
              endIcon={<ForwardIcon />}
              sx={{ bgcolor: ACCENT, color: '#fff', textTransform: 'none', fontWeight: 700, py: 1.4,
                boxShadow: '0 8px 24px rgba(9,105,218,0.28)', '&:hover': { bgcolor: ACCENT_DARK } }}
            >
              Open your workspace
            </Button>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: sub, mt: 1.5 }}>
              Sign in with the email and password you just created.
            </Typography>
          </Box>
        </Box>
        <Footer isDark={isDark} />
      </Box>
    );
  }

  if (verifySent) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: isDark ? '#0d1117' : SURFACE, color: isDark ? '#e6edf3' : INK }}>
        <TopBar isDark={isDark} loginHref={getAppUrl('/')} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Card sx={{ maxWidth: 460, width: '100%', p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: isDark ? '#30363d' : BORDER, bgcolor: isDark ? '#0d1117' : SURFACE }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Enter your code</Typography>
            <Typography sx={{ color: isDark ? '#9da7b3' : INK_SOFT, mb: 1 }}>
              We emailed a 6-character code to <strong>{adminEmail}</strong>. Enter it below to activate your account.
            </Typography>
            <TextField
              value={otp}
              onChange={(e) => { setOtp(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)); setOtpError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyOtp(); }}
              placeholder="ABC123"
              autoFocus
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.8rem', fontWeight: 800, letterSpacing: '0.5rem', fontFamily: 'monospace' } }}
              sx={{ my: 2.5, width: 240 }}
            />
            {otpError && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{otpError}</Alert>}
            {resendMsg && <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>{resendMsg}</Alert>}
            <Button
              fullWidth variant="contained" size="large"
              onClick={handleVerifyOtp}
              disabled={otpSubmitting || otp.length !== 6}
              endIcon={otpSubmitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
              sx={{ bgcolor: ACCENT, color: '#fff', textTransform: 'none', fontWeight: 700, py: 1.3, '&:hover': { bgcolor: ACCENT_DARK } }}
            >
              {otpSubmitting ? 'Verifying…' : 'Verify & continue'}
            </Button>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT }}>Didn't get it?</Typography>
              <Button size="small" onClick={handleResendOtp} sx={{ textTransform: 'none', color: ACCENT, fontWeight: 700 }}>
                Resend code
              </Button>
            </Stack>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: isDark ? '#0d1117' : SURFACE, color: isDark ? '#e6edf3' : INK }}>
      <TopBar isDark={isDark} loginHref={getAppUrl('/')} />

      {/* Hero — only on step 1. Step 2 jumps straight to the form. */}
      {step === 1 && (
        <Box
          sx={{
            position: 'relative',
            backgroundImage: isDark
              ? 'radial-gradient(circle at 50% 0%, rgba(9,105,218,0.18) 0%, rgba(13,17,23,0) 55%)'
              : 'radial-gradient(circle at 50% 0%, rgba(9,105,218,0.06) 0%, rgba(255,255,255,0) 55%)',
            pt: { xs: 5, md: 8 },
            pb: 3,
          }}
        >
          <Container maxWidth="lg">
            <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                label="Pricing"
                size="small"
                sx={{
                  bgcolor: ACCENT_SOFT,
                  color: ACCENT,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.025em',
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem' },
                  lineHeight: 1.1,
                  maxWidth: 760,
                }}
              >
                Pick the plan that fits your team.
              </Typography>
              <Typography
                sx={{
                  maxWidth: 580,
                  color: isDark ? '#9da7b3' : INK_SOFT,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.6,
                }}
              >
                Start free or jump straight to a paid tier. You can switch plans
                any time from billing settings — no migration, no downtime.
              </Typography>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Step content — uses a wider container on step 1 (3 plan cards
          fit nicely) and a narrower one on step 2 (form). */}
      <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
        <Container maxWidth={step === 1 ? 'lg' : 'md'}>
          {step !== 1 && (
            <Button
              startIcon={<BackIcon />}
              size="small"
              onClick={() => navigate('/')}
              sx={{ mb: 2, mt: 1, textTransform: 'none', color: isDark ? '#9da7b3' : INK_SOFT }}
            >
              Back to home
            </Button>
          )}

          <StepIndicator step={step} isDark={isDark} />

          {step === 1 ? (
            <PlanPickerStep
              plans={config.plans}
              selected={planCode}
              onSelect={setPlanCode}
              popularCode={popularPlanCode}
              error={error}
              onContinue={() => {
                const v = validateStep1();
                if (v) return setError(v);
                setError(null);
                setStep(2);
              }}
              onContactSales={(p) => setSalesPlan({ code: p.code, name: p.name })}
              isDark={isDark}
            />
          ) : (
            <DetailsStep
              selectedPlan={selectedPlan}
              orgName={orgName} setOrgName={setOrgName}
              orgSlug={orgSlug} setOrgSlug={(v) => { setOrgSlug(slugifyOrg(v)); setOrgSlugTouched(true); }}
              adminUsername={adminUsername} setAdminUsername={setAdminUsername}
              adminEmail={adminEmail} setAdminEmail={setAdminEmail}
              adminPassword={adminPassword} setAdminPassword={setAdminPassword}
              adminPasswordConfirm={adminPasswordConfirm} setAdminPasswordConfirm={setAdminPasswordConfirm}
              agree={agree} setAgree={setAgree}
              captchaToken={captchaToken} setCaptchaToken={setCaptchaToken}
              captcha={captcha}
              onChangePlan={() => { setStep(1); setError(null); }}
              error={error}
              submitting={submitting}
              onBack={() => { setStep(1); setError(null); }}
              onSubmit={handleSubmit}
              isDark={isDark}
            />
          )}
        </Container>
      </Box>

      <ContactSalesModal
        open={!!salesPlan}
        onClose={() => setSalesPlan(null)}
        planCode={salesPlan?.code}
        planName={salesPlan?.name}
        captcha={captcha}
        isDark={isDark}
      />

      <Footer isDark={isDark} />
    </Box>
  );
};

// ── Top bar (matches LandingPage / SignupSuccessPage) ────────────────
const TopBar = ({ isDark, loginHref }) => (
  <AppBar
    position="static"
    elevation={0}
    sx={{
      bgcolor: isDark ? 'rgba(13,17,23,0.92)' : 'rgba(255,255,255,0.92)',
      backdropFilter: 'saturate(180%) blur(8px)',
      borderBottom: '1px solid',
      borderColor: isDark ? '#30363d' : BORDER,
      color: isDark ? '#e6edf3' : INK,
    }}
  >
    <Container maxWidth="lg">
      <Toolbar disableGutters sx={{ height: 64, gap: 3 }}>
        <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: 10 }}>
          <Box component="img" src={LOGO_SRC} alt="VerOps" sx={{ height: 30, width: 'auto', display: 'block' }} />
        </RouterLink>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <Link href="/docs" underline="none" sx={{ fontSize: 14, fontWeight: 500, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Docs</Link>
          <Link href="/contact/new?type=GENERAL" underline="none" sx={{ fontSize: 14, fontWeight: 500, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Contact</Link>
        </Stack>
        <Button
          component="a"
          href={loginHref}
          variant="contained"
          size="small"
          sx={{
            bgcolor: ACCENT,
            color: '#fff',
            fontWeight: 500,
            textTransform: 'none',
            px: 2.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: ACCENT_DARK, boxShadow: 'none' },
          }}
          endIcon={<ChevronRightIcon fontSize="small" />}
        >
          Log in
        </Button>
      </Toolbar>
    </Container>
  </AppBar>
);

// ── Footer (matches LandingPage / SignupSuccessPage) ────────────────
const Footer = ({ isDark }) => (
  <Box
    component="footer"
    sx={{
      borderTop: '1px solid',
      borderColor: isDark ? '#30363d' : BORDER,
      py: 3,
      bgcolor: isDark ? '#0d1117' : SURFACE,
    }}
  >
    <Container maxWidth="lg">
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1.5} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box component="img" src={LOGO_SRC} alt="VerOps" sx={{ height: 22, width: 'auto', opacity: 0.85 }} />
          <Typography variant="caption" sx={{ color: isDark ? INK_DIM : INK_SOFT }}>
            © {new Date().getFullYear()} VerOps. Observability that connects.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2.5}>
          <Link href="/" underline="none" sx={{ fontSize: 13, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Home</Link>
          <Link href="/docs" underline="none" sx={{ fontSize: 13, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Docs</Link>
          <Link href="/contact/new?type=GENERAL" underline="none" sx={{ fontSize: 13, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Contact</Link>
        </Stack>
      </Stack>
    </Container>
  </Box>
);

// ── Step indicator (1 → 2) ────────────────────────────────────────────
const StepIndicator = ({ step, isDark }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4, justifyContent: 'center' }}>
    {[
      { n: 1, label: 'Choose plan' },
      { n: 2, label: 'Account details' },
    ].map((s, idx) => (
      <Stack key={s.n} direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '0.85rem',
            bgcolor: step >= s.n ? ACCENT : (isDark ? 'transparent' : '#fff'),
            border: '2px solid',
            borderColor: step >= s.n ? ACCENT : (isDark ? '#30363d' : BORDER),
            color: step >= s.n ? '#fff' : (isDark ? '#9da7b3' : INK_SOFT),
          }}
        >
          {step > s.n ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : s.n}
        </Box>
        <Typography variant="body2" fontWeight={step === s.n ? 700 : 500} sx={{ color: step === s.n ? ACCENT : (isDark ? '#9da7b3' : INK_SOFT) }}>
          {s.label}
        </Typography>
        {idx === 0 && (
          <Box sx={{ width: 36, height: '2px', bgcolor: isDark ? '#30363d' : BORDER, mx: 1 }} />
        )}
      </Stack>
    ))}
  </Stack>
);

// ── Step 1: plan picker ─────────────────────────────────────────────
const PlanPickerStep = ({ plans, selected, onSelect, popularCode, error, onContinue, onContactSales, isDark }) => (
  <Box>
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: {
          xs: '1fr',
          md: plans.length === 1 ? '1fr' : (plans.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
          lg: plans.length === 1 ? '1fr' : (plans.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
        },
        maxWidth: 1100,
        mx: 'auto',
        alignItems: 'stretch',
      }}
    >
      {plans.map((p) => (
        <PlanCard
          key={p.code}
          plan={p}
          selected={selected === p.code}
          popular={popularCode === p.code}
          onSelect={() => onSelect(p.code)}
          onContactSales={() => onContactSales?.(p)}
          isDark={isDark}
        />
      ))}
    </Box>
    {error && <Alert severity="error" sx={{ mt: 3, maxWidth: 760, mx: 'auto' }}>{error}</Alert>}
    {(() => {
      const sel = plans.find((p) => p.code === selected);
      const isEnterprise = sel && sel.enterprise;
      return (
        <Stack direction="row" justifyContent="center" sx={{ mt: 4, mb: 6 }}>
          {isEnterprise ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => onContactSales?.(sel)}
              sx={{
                bgcolor: ACCENT, color: '#fff', fontWeight: 700, textTransform: 'none',
                px: 5, py: 1.5, fontSize: '1rem', boxShadow: '0 8px 24px rgba(9,105,218,0.28)',
                '&:hover': { bgcolor: ACCENT_DARK },
              }}
            >
              Talk with sales
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              endIcon={<ForwardIcon />}
              onClick={onContinue}
              sx={{
                bgcolor: ACCENT, color: '#fff', fontWeight: 700, textTransform: 'none',
                px: 5, py: 1.5, fontSize: '1rem', boxShadow: '0 8px 24px rgba(9,105,218,0.28)',
                '&:hover': { bgcolor: ACCENT_DARK, boxShadow: '0 8px 24px rgba(9,105,218,0.32)' },
              }}
            >
              {sel && sel.freeTier ? `Start ${sel.trialDays || 14}-day free trial` : 'Continue'}
            </Button>
          )}
        </Stack>
      );
    })()}
  </Box>
);

// The plan's `maxEventsPerDay` is stored as BYTES/day (the operator enters it
// as "Data per day (MB)" → ×1024²). Render it as a human data volume.
const formatBytesPerDay = (bytes) => {
  if (bytes == null) return '';
  const tb = bytes / 1024 ** 4;
  if (tb >= 1) return `${+tb.toFixed(tb % 1 ? 1 : 0)} TB`;
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${+gb.toFixed(gb % 1 ? 1 : 0)} GB`;
  const mb = bytes / 1024 ** 2;
  if (mb >= 1) return `${+mb.toFixed(mb % 1 ? 1 : 0)} MB`;
  const kb = bytes / 1024;
  if (kb >= 1) return `${+kb.toFixed(0)} KB`;
  return `${bytes} B`;
};

// ── Plan card ────────────────────────────────────────────────────────
const PlanCard = ({ plan, selected, popular, onSelect, onContactSales, isDark }) => {
  const features = useMemo(() => {
    // Enterprise is a "talk to sales" tier — show ONLY qualitative
    // capabilities, never numeric limits or pricing.
    if (plan.enterprise) {
      const out = ['Custom users, agents & ingestion', 'Long-term data retention'];
      if (plan.ssoEnabled) out.push('Single sign-on (SSO)');
      if (plan.sessionReplayEnabled) out.push('Session replay');
      out.push('Dedicated cluster routing');
      if (plan.auditLogEnabled) out.push('Audit log');
      out.push('Priority support & onboarding');
      return out;
    }
    const out = [];
    if (plan.maxUsers) out.push(`${plan.maxUsers === -1 ? 'Unlimited' : plan.maxUsers} users`);
    if (plan.maxAgents) out.push(`${plan.maxAgents === -1 ? 'Unlimited' : plan.maxAgents} agents`);
    if (plan.maxTests) out.push(`${plan.maxTests === -1 ? 'Unlimited' : plan.maxTests} synthetic tests`);
    if (plan.ingestCapMode === 'MONTHLY') {
      const m = plan.maxEventsPerMonth;
      if (m != null) out.push(`${m === -1 ? 'Unlimited' : formatBytesPerDay(m)} data/month ingestion`);
    } else if (plan.maxEventsPerDay) {
      out.push(`${plan.maxEventsPerDay === -1 ? 'Unlimited' : formatBytesPerDay(plan.maxEventsPerDay)} data/day ingestion`);
    }
    if (plan.sessionReplayEnabled) out.push('Session replay');
    if (plan.ssoEnabled) out.push('Single sign-on (SSO)');
    if (plan.auditLogEnabled) out.push('Audit log');
    if (plan.freeTier) out.push(`${plan.trialDays || 14}-day free trial`);
    else if (!plan.requiresPayment) out.push('Free forever');
    return out;
  }, [plan]);

  return (
    <Card
      onClick={onSelect}
      role="button"
      sx={{
        position: 'relative',
        p: { xs: 3, md: 3.5 },
        cursor: 'pointer',
        borderRadius: 3,
        border: '2px solid',
        borderColor: selected ? ACCENT : (popular ? (isDark ? '#3a4654' : '#b3c5e0') : (isDark ? '#30363d' : BORDER)),
        bgcolor: selected
          ? (isDark ? 'rgba(9,105,218,0.06)' : ACCENT_SOFT)
          : (isDark ? '#0d1117' : SURFACE),
        boxShadow: popular && !selected
          ? (isDark ? '0 8px 20px rgba(0,0,0,0.3)' : '0 8px 24px rgba(15,23,42,0.06)')
          : 'none',
        transform: popular ? 'translateY(-4px)' : 'none',
        transition: 'border-color 120ms, transform 120ms, box-shadow 120ms',
        '&:hover': {
          borderColor: ACCENT,
        },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Popular ribbon */}
      {popular && (
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: ACCENT,
            color: '#fff',
            px: 1.5,
            py: 0.4,
            borderRadius: 999,
            fontSize: '0.7rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            boxShadow: '0 4px 12px rgba(9,105,218,0.28)',
          }}
        >
          <StarIcon sx={{ fontSize: 14 }} /> Most popular
        </Box>
      )}

      {/* Selected check */}
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 24, height: 24,
            borderRadius: '50%',
            bgcolor: ACCENT,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckRoundedIcon sx={{ fontSize: 16 }} />
        </Box>
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <PlanIcon sx={{ color: ACCENT, fontSize: 22 }} />
        <Typography variant="overline" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: '0.06em' }}>
          {plan.name}
        </Typography>
      </Stack>

      {plan.description && (
        <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, mb: 2.5, minHeight: 38 }}>
          {plan.description}
        </Typography>
      )}

      {/* Big price — enterprise advertises no price, just "Let's talk". */}
      <Box sx={{ mb: 2.5 }}>
        {plan.enterprise ? (
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
              Let's talk
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, mt: 0.25 }}>
              Custom pricing for your scale
            </Typography>
          </Box>
        ) : plan.displayPrice ? (
          <PriceDisplay raw={plan.displayPrice} isDark={isDark} />
        ) : plan.requiresPayment ? (
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
            Custom
          </Typography>
        ) : (
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', color: SUCCESS }}>
            Free
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 1.5, borderColor: isDark ? '#21262d' : '#eaeef2' }} />

      <Stack spacing={1.25} sx={{ flex: 1 }}>
        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: isDark ? '#9da7b3' : INK_SOFT, fontSize: '0.7rem' }}>
          What's included
        </Typography>
        {features.map((f) => (
          <Stack key={f} direction="row" alignItems="flex-start" spacing={1}>
            <CheckIcon sx={{ fontSize: 16, color: SUCCESS, mt: 0.25 }} />
            <Typography variant="body2" sx={{ color: isDark ? '#e6edf3' : INK }}>
              {f}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Button
        variant={plan.enterprise ? 'outlined' : (selected ? 'contained' : 'outlined')}
        fullWidth
        onClick={(e) => {
          e.stopPropagation();
          if (plan.enterprise) onContactSales?.();
          else onSelect();
        }}
        sx={{
          mt: 3,
          textTransform: 'none',
          fontWeight: 700,
          py: 1,
          ...((selected && !plan.enterprise) ? {
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: ACCENT_DARK, boxShadow: 'none' },
          } : {
            borderColor: isDark ? '#30363d' : BORDER,
            color: isDark ? '#e6edf3' : INK,
            '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: 'transparent' },
          }),
        }}
      >
        {plan.enterprise ? 'Contact sales' : (selected ? 'Selected' : (plan.requiresPayment ? 'Choose this plan' : 'Start free'))}
      </Button>
    </Card>
  );
};

// Render "$49/month" as a big number + small "/month" suffix.
const PriceDisplay = ({ raw, isDark }) => {
  const match = raw && typeof raw === 'string' ? raw.match(/^(.*?\d[\d,.]*)(\s*\/\s*\w+)?$/) : null;
  if (!match) {
    return (
      <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
        {raw}
      </Typography>
    );
  }
  const amount = match[1].trim();
  const suffix = match[2] ? match[2].trim() : '';
  return (
    <Stack direction="row" alignItems="baseline" spacing={0.75}>
      <Typography sx={{ fontWeight: 800, letterSpacing: '-0.025em', fontSize: { xs: '2rem', md: '2.4rem' }, lineHeight: 1 }}>
        {amount}
      </Typography>
      {suffix && (
        <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, fontWeight: 600 }}>
          {suffix}
        </Typography>
      )}
    </Stack>
  );
};

// ── Step 2: workspace + admin form ─────────────────────────────────
const DetailsStep = ({
  selectedPlan,
  orgName, setOrgName,
  orgSlug, setOrgSlug,
  adminUsername, setAdminUsername,
  adminEmail, setAdminEmail,
  adminPassword, setAdminPassword,
  adminPasswordConfirm, setAdminPasswordConfirm,
  agree, setAgree,
  captchaToken, setCaptchaToken,
  captcha,
  onChangePlan,
  error, submitting,
  onBack, onSubmit,
  isDark,
}) => (
  <Box>
    <Stack alignItems="center" spacing={1} sx={{ mb: 4, textAlign: 'center' }}>
      <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.02em', fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
        Tell us about your team.
      </Typography>
      <Typography sx={{ color: isDark ? '#9da7b3' : INK_SOFT, fontSize: '1rem' }}>
        Two minutes and you're inside.
      </Typography>
    </Stack>

    <Card variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3, mb: 3, borderColor: isDark ? '#30363d' : BORDER }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
        <OrgIcon sx={{ color: ACCENT }} />
        <Typography variant="h6" fontWeight={800}>Workspace</Typography>
      </Stack>
      <Stack spacing={2}>
        <TextField
          label="Organisation name"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          fullWidth required autoFocus
          inputProps={{ maxLength: 100 }}
          helperText="Visible to your team in the platform — e.g. Acme Inc."
        />
        <TextField
          label="Workspace URL slug"
          value={orgSlug}
          onChange={(e) => setOrgSlug(e.target.value)}
          fullWidth required
          inputProps={{ maxLength: 50 }}
          helperText={`Lowercase letters, digits, and dashes. Used in your platform URL: app.verops.io/${orgSlug || 'your-slug'}`}
        />
      </Stack>
    </Card>

    <Card variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3, mb: 3, borderColor: isDark ? '#30363d' : BORDER }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
        <UserIcon sx={{ color: ACCENT }} />
        <Typography variant="h6" fontWeight={800}>Admin account</Typography>
      </Stack>
      <Stack spacing={2}>
        <TextField
          label="Username (optional)"
          value={adminUsername}
          onChange={(e) => setAdminUsername(e.target.value)}
          fullWidth
          inputProps={{ maxLength: 50 }}
          helperText="You'll sign in with this (or your email). Also shown to teammates. Defaults to your email."
        />
        <TextField
          label="Work email"
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          fullWidth required
          helperText="You can also log in with this address."
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Password"
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            fullWidth required
            inputProps={{ minLength: 8 }}
            helperText="At least 8 characters."
          />
          <TextField
            label="Confirm password"
            type="password"
            value={adminPasswordConfirm}
            onChange={(e) => setAdminPasswordConfirm(e.target.value)}
            fullWidth required
          />
        </Stack>
      </Stack>
    </Card>

    {selectedPlan && (
      <Card variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, mb: 3, borderColor: isDark ? '#30363d' : BORDER, bgcolor: isDark ? 'rgba(9,105,218,0.04)' : ACCENT_SOFT }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: 1.5,
              bgcolor: isDark ? 'rgba(110,168,254,0.16)' : 'rgba(9,105,218,0.12)',
              color: ACCENT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PaymentIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: isDark ? '#9da7b3' : INK_SOFT, fontSize: '0.7rem' }}>
              You're signing up for
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1.5}>
              <Typography variant="h6" fontWeight={800}>{selectedPlan.name}</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, fontWeight: 600 }}>
                {selectedPlan.displayPrice || (selectedPlan.requiresPayment ? 'Paid plan' : 'Free')}
              </Typography>
            </Stack>
          </Box>
          <Button size="small" onClick={onChangePlan} sx={{ textTransform: 'none', color: ACCENT }}>
            Change plan
          </Button>
        </Stack>
        {selectedPlan.requiresPayment && (
          <Alert severity="info" sx={{ mt: 2 }}>
            After clicking <strong>Create account</strong>, we'll redirect you to Stripe to complete payment.
            Your account is provisioned immediately and the upgrade applies once Stripe confirms.
          </Alert>
        )}
      </Card>
    )}

    {captcha?.enabled && captcha.provider && (
      <Box sx={{ mb: 2 }}>
        <CaptchaWidget
          provider={captcha.provider}
          siteKey={captcha.siteKey}
          onVerify={setCaptchaToken}
          onExpire={() => setCaptchaToken(null)}
        />
      </Box>
    )}

    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 3 }}>
      <input
        id="agree"
        type="checkbox"
        checked={agree}
        onChange={(e) => setAgree(e.target.checked)}
        style={{ marginTop: 4, width: 16, height: 16, accentColor: ACCENT }}
      />
      <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, lineHeight: 1.55 }} component="label" htmlFor="agree">
        I agree to the VerOps{' '}
        <Link
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: ACCENT, fontWeight: 600, textDecoration: 'underline' }}
        >
          Terms of Service
        </Link>
        {' '}and Privacy Policy.
      </Typography>
    </Stack>

    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

    <Stack direction="row" spacing={1.5}>
      <Button onClick={onBack} disabled={submitting} sx={{ textTransform: 'none' }}>
        Back
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={submitting}
        endIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ForwardIcon />}
        sx={{
          bgcolor: ACCENT,
          color: '#fff',
          fontWeight: 700,
          textTransform: 'none',
          py: 1.5,
          px: 3,
          boxShadow: '0 8px 24px rgba(9,105,218,0.28)',
          '&:hover': { bgcolor: ACCENT_DARK, boxShadow: '0 8px 24px rgba(9,105,218,0.32)' },
        }}
      >
        {submitting ? 'Creating…' : selectedPlan?.requiresPayment ? 'Create & continue to payment' : 'Create account'}
      </Button>
    </Stack>
  </Box>
);

export default SignupPage;
